import {Avatar, Button, message, Modal, Radio, Spin, Tag} from 'antd'
import {
    course_dismissDuplicate_Api,
    course_executeMerge_Api,
    course_getDuplicates_Api,
    course_mergePreview_Api,
    course_scanDuplicates_Api,
} from 'apis'
import classNames from 'classnames'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useEffect, useState} from 'react'
import {BiMerge} from 'react-icons/bi'
import {MdOutlinePersonOff, MdSwapHoriz} from 'react-icons/md'
import {PiStudentFill, PiWarningCircleFill} from 'react-icons/pi'
import {TbArrowRight} from 'react-icons/tb'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import {formatSimplifyDate} from "../../../../../helpers/dateUtils";
import {GoDotFill} from "react-icons/go";
import _ from "lodash";
import {IdentifierTypes} from "../../../../AppLocals/appFieldTypes";
import {LayoutContext} from "../../../../../components/layouts/Default/LayoutContext";
import {getInitials} from "../../functions/renderStudent";
import {getGroupColor} from "../../functions/renderCourseClassStudent";

const REASON_LABELS = {
    identifier_exact: 'match reason identifier_exact',
    name: 'match reason name',
    phone: 'match reason phone',
    email: 'match reason email',
    dob: 'match reason dob',
}

const UNIQUE_LINK_LABELS = {
    SELF: 'self (student)',
    FATHER: 'father',
    MOTHER: 'mother',
}

const StudentSummaryCard = ({student, role, t}) => {

    const {isLg} = useContext(LayoutContext)
    const isArchived = role === 'archived'

    if (!student) return null

    return (
        <div
            className={classNames(
                'flex flex-col gap-2 p-3 background rounded-xl border-2 flex-1 min-w-0',
                isArchived ? 'border-orange-300' : 'border-green-300'
            )}>
            <div
                className={classNames(
                    'px-3 py-1 font-bold uppercase tracking-wider text-xs rounded-full self-start',
                    isArchived ? 'bg-orange-200 text-orange-700' : 'bg-green-200 text-green-700'
                )}>
                {t(isArchived ? 'will be archived' : 'master student')}
            </div>
            <div className="flex items-center gap-2">
                <div
                    className={classNames(
                        'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
                        isArchived ? 'bg-orange-100' : 'bg-green-100'
                    )}>
                    <PiStudentFill
                        size={16}
                        className={isArchived ? 'text-orange-500' : 'text-green-600'}
                    />
                </div>
                <div className="min-w-0">
                    <div className="font-bold text-sm truncate">{student.fullname}</div>
                    {student.student_idcode && (
                        <div className="text-xs text-blue-500 font-mono">#{student.student_idcode}</div>
                    )}
                    {student.ref_id && (
                        <div className="text-xs text-secondary-500 font-mono">
                            {t('Ref')}: {student.ref_id}
                        </div>
                    )}
                </div>
            </div>
            {student.date_of_birth && (
                <div className="font-medium text-xs text-color-100">
                    <GoDotFill size={10} className="mr-1 text-color-500"/>
                    {isLg && (
                        <span className="font-normal text-color-400">
                            {`${t('date of birth')}: `}
                        </span>
                    )}
                    {formatSimplifyDate(student.date_of_birth)}
                </div>
            )}
            {student.identifier_code && (
                <div className="font-medium text-xs text-color-100 font-mono">
                    <GoDotFill size={10} className="mr-1 text-color-500"/>
                    {isLg && (
                        <span className="font-normal text-color-400">
                            {`${t(_.find(IdentifierTypes, {value: student?.identifier_type})?.label)}: `}
                        </span>
                    )}
                    {student.identifier_code}
                </div>
            )}
            {student.contact_phone && (
                <div className="font-medium text-xs text-color-100">
                    <GoDotFill size={10} className="mr-1 text-color-500"/>
                    {isLg && (
                        <span className="font-normal text-color-400">
                            {`${t('phone')}: `}
                        </span>
                    )}
                    {student.contact_phone}
                </div>
            )}
            <div className="mt-1 pt-1 border-t border-color-50 flex gap-3 font-semibold text-xs text-color-100">
                {student.course_count != null && (
                    <span>{student.course_count} - {t('courses')}</span>
                )}
                {student.course_class_count != null && (
                    <span>{student.course_class_count} - {t('classes')}</span>
                )}
            </div>
        </div>
    )
}

const MergeModal = ({visible, pair, onClose, onMerged, t}) => {
    const [preview, setPreview] = useState(null)
    const [loadingPreview, setLoadingPreview] = useState(false)
    const [resolutions, setResolutions] = useState({user_links: {}, enrollments: {}, ref_id: 'discard'})
    const [swapped, setSwapped] = useState(false)

    const archived = swapped ? pair?.candidate : pair?.student
    const master = swapped ? pair?.student : pair?.candidate

    const {handleAsyncAction: fetchPreview} = useAsyncAction({
        apiInfo: course_mergePreview_Api,
        onSuccess: (_, meta) => {
            setPreview(meta?.response?.data ?? null)
            setLoadingPreview(false)
        },
        onError: () => setLoadingPreview(false),
    })

    const {handleAsyncAction: doMerge, isLoading: merging} = useAsyncAction({
        apiInfo: course_executeMerge_Api,
        onSuccess: () => {
            message.success(t('merge students') + ' ' + t('success'))
            onMerged()
            onClose()
        },
        onError: () => message.error(t('failed')),
    })

    useEffect(() => {
        if (!visible || !pair || !archived || !master) return
        setLoadingPreview(true)
        setResolutions({user_links: {}, enrollments: {}, ref_id: 'discard'})
        fetchPreview({}, {id: archived.id, target_id: master.id})
    }, [visible, pair, swapped])

    const handleMerge = useCallback(() => {
        if (!archived || !master) return
        doMerge(
            {conflict_resolutions: JSON.stringify(resolutions)},
            {id: archived.id, target_id: master.id}
        )
    }, [archived, master, resolutions])

    const conflicts = preview?.conflicts ?? {user_links: [], enrollments: [], ref_id: null}
    const hasConflicts =
        conflicts.user_links.length > 0 || conflicts.enrollments.length > 0 || !!conflicts.ref_id

    const previewArchived = preview?.archived
    const previewMaster = preview?.master

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            className="custom-modal"
            title={
                <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                    {t('merge preview')}
                </div>
            }
            footer={null}
            width={720}
            destroyOnHidden>
            {loadingPreview ? (
                <div className="flex justify-center py-10">
                    <Spin/>
                </div>
            ) : (
                <div className="flex flex-col gap-4 py-2">
                    {/* Master / Archived side-by-side */}
                    <div className="flex items-stretch gap-2">
                        <StudentSummaryCard
                            student={previewArchived ?? archived}
                            role="archived"
                            t={t}
                        />
                        <div className="flex flex-col items-center justify-center gap-1 flex-shrink-0 px-1">
                            <TbArrowRight size={22} className="text-color-300"/>
                            <button
                                onClick={() => setSwapped((s) => !s)}
                                style={{ marginTop: '0.25rem', padding: '0.125rem 0.5rem', borderRadius: '0.375rem'}}
                                className="flex items-center gap-1 text-xs text-color-300 hover:text-primary border border-color-100 hover:border-primary transition-all">
                                <MdSwapHoriz size={14}/>
                                {t('swap roles')}
                            </button>
                        </div>
                        <StudentSummaryCard
                            student={previewMaster ?? master}
                            role="master"
                            t={t}
                        />
                    </div>

                    {/* What transfers */}
                    <div className="px-3 py-2 border border-blue-400 rounded-xl">
                        <div className="mb-1 font-semibold text-blue-600 tracking-wide">
                            {t('what will transfer to master')}:
                        </div>
                        <ul style={{paddingLeft: 16, listStyleType: 'disc'}} className="ml-1 space-y-1 italic">
                            <li className="text-color-100">{t('all enrollments and class assignments')}</li>
                            <li className="text-color-100">{t('user links and family relationships')}</li>
                            <li className="text-color-100">{t('grades, attendance, and certificates')}</li>
                        </ul>
                    </div>

                    {/* Conflicts */}
                    {hasConflicts ? (
                        <div className="flex flex-col gap-3">
                            <div className="font-semibold text-sm text-orange-600 flex items-center gap-1.5">
                                <PiWarningCircleFill size={16}/>
                                {t('conflicts to resolve')}
                            </div>
                            {conflicts.user_links.map((c) => (
                                <div
                                    key={c.archived_link?.id}
                                    className="rounded-lg border border-orange-200 bg-orange-50 p-3 flex flex-col gap-2">
                                    <span className="text-xs font-semibold text-orange-700">
                                        {t(UNIQUE_LINK_LABELS[c.relationship] ?? c.relationship)} — {t('conflicts to resolve')}
                                    </span>
                                    <Radio.Group
                                        size="small"
                                        value={resolutions.user_links[c.archived_link?.id] ?? 'discard'}
                                        onChange={(e) =>
                                            setResolutions((r) => ({
                                                ...r,
                                                user_links: {...r.user_links, [c.archived_link?.id]: e.target.value},
                                            }))
                                        }>
                                        <Radio value="discard">{t('keep master')}</Radio>
                                        <Radio value="keep">{t('keep archived')}</Radio>
                                    </Radio.Group>
                                </div>
                            ))}

                            {conflicts.ref_id && (
                                <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 flex flex-col gap-2">
                                    <span className="text-xs font-semibold text-orange-700">
                                        {t('ref id')} — {t('conflicts to resolve')}
                                    </span>
                                    <Radio.Group
                                        size="small"
                                        value={resolutions.ref_id ?? 'discard'}
                                        onChange={(e) =>
                                            setResolutions((r) => ({...r, ref_id: e.target.value}))
                                        }>
                                        <Radio value="discard">
                                            {t('keep master')} ({conflicts.ref_id.master_value})
                                        </Radio>
                                        <Radio value="keep">
                                            {t('keep archived')} ({conflicts.ref_id.archived_value})
                                        </Radio>
                                    </Radio.Group>
                                </div>
                            )}

                            {conflicts.enrollments.map((c) => (
                                <div
                                    key={c.archived_enrollment?.id}
                                    className="rounded-lg border border-orange-200 bg-orange-50 p-3 flex flex-col gap-2">
                                    <span className="text-xs font-semibold text-orange-700">
                                        {t('class enrollment conflict')} — {t('conflicts to resolve')}
                                    </span>
                                    <Radio.Group
                                        size="small"
                                        value={resolutions.enrollments[c.archived_enrollment?.id] ?? 'discard'}
                                        onChange={(e) =>
                                            setResolutions((r) => ({
                                                ...r,
                                                enrollments: {
                                                    ...r.enrollments,
                                                    [c.archived_enrollment?.id]: e.target.value,
                                                },
                                            }))
                                        }>
                                        <Radio value="discard">{t('keep master')}</Radio>
                                        <Radio value="keep">{t('keep archived')}</Radio>
                                    </Radio.Group>
                                </div>
                            ))}
                        </div>
                    ) : preview ? (
                        <div className="text-sm text-green-600 flex items-center gap-1.5">
                            ✓ {t('no conflicts')}
                        </div>
                    ) : null}

                    <div className="flex justify-end gap-2 pt-2 border-t border-color-50">
                        <Button onClick={onClose}>{t('cancel')}</Button>
                        <Button
                            type="primary"
                            danger
                            loading={merging}
                            onClick={handleMerge}>
                            {t('confirm merge')}
                        </Button>
                    </div>
                </div>
            )}
        </Modal>
    )
}

const StudentCard = ({student, t}) => {

    const {isLg} = useContext(LayoutContext)
    const initials = getInitials(student?.fullname)
    const color = getGroupColor(student?.fullname)

    if (!student) return null

    return (
        <div className="flex flex-col gap-1 rounded-lg bg-color-50 min-w-0 flex-1">
            <div className="flex items-center gap-2">
                {student?.avatar ? (
                    <Avatar
                        size={36}
                        src={student?.avatar}
                        className="rounded-full"
                    />
                ) : (
                    <div
                        style={{width: 36, height: 36, backgroundColor: color, flexShrink: 0}}
                        className="rounded-full flex flex-center">
                        <span className="text-white font-bold text-xs leading-none">{initials}</span>
                    </div>
                )}
                <div className="min-w-0">
                    <div className="font-bold text-sm text-gray-800 tracking-wide truncate">{student.fullname}</div>
                    {student.student_idcode && (
                        <div className="font-mono text-xs text-blue-500">#{student.student_idcode}</div>
                    )}
                </div>
            </div>
            {student.date_of_birth && (
                <div className="font-medium text-xs text-color-100">
                    <GoDotFill size={10} className="mr-1 text-color-500"/>
                    {isLg && (
                        <span className="font-normal text-color-400">
                            {`${t('date of birth')}: `}
                        </span>
                    )}
                    {formatSimplifyDate(student.date_of_birth)}
                </div>
            )}
            {student.identifier_code && (
                <div className="font-medium text-xs text-color-100 font-mono">
                    <GoDotFill size={10} className="mr-1 text-color-500"/>
                    {isLg && (
                        <span className="font-normal text-color-400">
                            {`${t(_.find(IdentifierTypes, {value: student?.identifier_type})?.label)}: `}
                        </span>
                    )}
                    {student.identifier_code}
                </div>
            )}
            {student.contact_phone && (
                <div className="font-medium text-xs text-color-100">
                    <GoDotFill size={10} className="mr-1 text-color-500"/>
                    {isLg && (
                        <span className="font-normal text-color-400">
                            {`${t('phone')}: `}
                        </span>
                    )}
                    {student.contact_phone}
                </div>
            )}
        </div>
    )
}

const DuplicateStudents = ({owner_id, owner_type}) => {
    const t = useTranslate()

    const [items, setItems] = useState([])
    const [loaded, setLoaded] = useState(false)
    const [hasBeenScanned, setHasBeenScanned] = useState(false)
    const [lastScanned, setLastScanned] = useState(null)
    const [mergeTarget, setMergeTarget] = useState(null)

    const {handleAsyncAction: loadDuplicates, isLoading} = useAsyncAction({
        apiInfo: course_getDuplicates_Api,
        onSuccess: (_, meta) => {
            const data = meta?.response?.data
            setItems(data?.items ?? [])
            setLastScanned(data?.last_scan_at ?? null)
            setHasBeenScanned(data?.has_been_scanned ?? !!data?.last_scan_at)
            setLoaded(true)
        },
    })

    const {handleAsyncAction: doScan, isLoading: scanning} = useAsyncAction({
        apiInfo: course_scanDuplicates_Api,
        onSuccess: (_, meta) => {
            const data = meta?.response?.data
            message.success(
                `${t('flagged students')}: ${data?.flagged ?? 0}`
            )
            loadDuplicates({}, {id: owner_id, type: owner_type})
        },
    })

    const {handleAsyncAction: doDismiss} = useAsyncAction({
        apiInfo: course_dismissDuplicate_Api,
        onSuccess: () => {
            message.info(t('dismiss duplicate'))
            loadDuplicates({}, {id: owner_id, type: owner_type})
        },
    })

    useEffect(() => {
        if (owner_id && owner_type) {
            loadDuplicates({}, {id: owner_id, type: owner_type})
        }
    }, [owner_id, owner_type])

    const handleRefresh = useCallback(() => {
        loadDuplicates({}, {id: owner_id, type: owner_type})
    }, [owner_id, owner_type])

    return (
        <div className="flex flex-col gap-4">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="flex flex-col flex-1">
                    <h2 className="font-bold text-xl text-gray-800 tracking-wide">
                      {t('duplicate students')}
                    </h2>
                    <div className="my-1 w-20 h-1 rounded-full bg-gray-400"/>
                    {lastScanned && (
                        <span className="mt-1 text-sm text-color-300 tracking-wider">
                            {t('last scanned')}: {formatSimplifyDate(lastScanned, 'fullTime')}
                        </span>
                    )}
                </div>
                <Button
                    color="primary" variant="solid"
                    loading={scanning}
                    onClick={() => doScan({}, {id: owner_id, type: owner_type})}
                    className="rounded-lg">
                    {t('scan for duplicates')}
                </Button>
            </div>

            {/* List */}
            {(!loaded && isLoading) ? (
                <div className="flex justify-center py-12">
                    <Spin/>
                </div>
            ) : items.length === 0 ? (
                <div
                    className="flex flex-col items-center gap-4 py-14 rounded-xl border border-dashed border-color-100">
                    <MdOutlinePersonOff size={44} className="text-color-300 opacity-40"/>
                    {!hasBeenScanned ? (
                        <>
                            <span className="text-sm text-color-400 font-medium">{t('no scan yet')}</span>
                            <Button
                                type="primary"
                                loading={scanning}
                                onClick={() => doScan({}, {id: owner_id, type: owner_type})}>
                                {t('scan for duplicates')}
                            </Button>
                        </>
                    ) : (
                        <span className="text-sm text-color-400 italic">{t('no duplicates found')}</span>
                    )}
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    {items.map((pair, i) => {
                        const score = pair.dup_score ?? 0
                        const isHigh = pair.high_confidence
                        const reasons = pair.dup_reasons ?? []

                        return (
                            <div
                                key={i}
                                className={classNames(
                                    'p-3 md:p-4 flex flex-col gap-3 background hover:shadow-md rounded-2xl border',
                                    isHigh ? 'border-green-400' : 'border-color-100'
                                )}>
                                {/* Score + reasons */}
                                <div className="flex flex-wrap items-center gap-2">
                                    <Tag
                                        color={isHigh ? 'green' : 'orange'}
                                        className="px-2 font-bold text-sm">
                                        {score}%
                                    </Tag>
                                    {isHigh && (
                                        <Tag color="green" className="flex items-center gap-1">
                                            <PiWarningCircleFill size={13}/>
                                            {t('high confidence duplicate')}
                                        </Tag>
                                    )}
                                    {reasons.map((r) => (
                                        <Tag key={r} color="blue" className="flex items-center gap-1">
                                            {t(REASON_LABELS[r] ?? r)}
                                        </Tag>
                                    ))}
                                </div>

                                {/* Side by side */}
                                <div className="flex gap-3">
                                    <StudentCard student={pair.student} t={t}/>
                                    <div className="flex items-center justify-center px-1 text-color-300 text-lg">
                                        ↔
                                    </div>
                                    <StudentCard student={pair.candidate} t={t}/>
                                </div>

                                {/* Actions */}
                                <div className="mt-3 flex justify-end gap-2">
                                    <Button
                                        color="default" variant="filled"
                                        icon={<MdOutlinePersonOff size={14}/>}
                                        onClick={() => doDismiss({}, {id: pair.student.id})}
                                        className="text-xs rounded-lg">
                                        {t('dismiss duplicate')}
                                    </Button>
                                    <Button
                                        color="primary" variant="solid"
                                        icon={<BiMerge size={14}/>}
                                        onClick={() => setMergeTarget(pair)}
                                        className="text-xs rounded-lg">
                                        {t('merge students')}
                                    </Button>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}

            <MergeModal
                key={mergeTarget ? `${mergeTarget.student.id}-${mergeTarget.candidate.id}` : ''}
                visible={!!mergeTarget}
                pair={mergeTarget}
                onClose={() => setMergeTarget(null)}
                onMerged={handleRefresh}
                t={t}
            />
            <div className="h-16"/>
        </div>
    )
}

export default DuplicateStudents
